import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Box,
  Button,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { BancoDeDados } from '../../../../../shared/services/api/c4-model/SistemasService';
import { ModalBancoDeDados } from './ModalBancoDeDados';
import { TiposBancoDadosService } from '../../../../../shared/services/api/c4-model/TiposBancoDadosService';
import { Skeleton } from '@mui/material';

type TipoBancoDados = {
  id: number;
  nome: string;
};

type Row = {
  id: string;
  tipo: number;
};

type Props = {
  initialRows?: BancoDeDados[];
  onBancosDeDadosChange?: (bancos: BancoDeDados[]) => void;
  currentSystemId?: string;
  isLoading?: boolean;
};

const convertToRows = (bancos: BancoDeDados[]): Row[] => {
  return (bancos || []).map((item) => ({
    id: item.id || String(Date.now()),
    tipo: item.tipo,
  }));
};

export const ListaBancoDeDados: React.FC<Props> = ({
  initialRows = [],
  onBancosDeDadosChange,
  currentSystemId,
  isLoading = false,
}) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [editingRow, setEditingRow] = useState<Row | null>(null);
  const [tiposBancoDados, setTiposBancoDados] = useState<TipoBancoDados[]>([]);

  useEffect(() => {
    setRows(convertToRows(initialRows || []));
  }, [initialRows]);

  useEffect(() => {
    // Carregar tipos de banco de dados
    TiposBancoDadosService.getAll().then((data) => {
      if (Array.isArray(data)) {
        setTiposBancoDados(data);
      }
    });
  }, []);

  // Função para obter o nome do tipo de banco de dados
  const getTipoBancoDadosNome = (id: number): string => {
    const tipoBd = tiposBancoDados.find((t) => t.id === id);
    return tipoBd?.nome || `Tipo ${id}`;
  };

  const handleEdit = (id: string) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    setEditingRow(row);
    setOpenModal(true);
  };

  const handleDelete = (id: string) => {
    const newRows = rows.filter((r) => r.id !== id);
    setRows(newRows);

    if (onBancosDeDadosChange) {
      const updatedBancos = newRows.map((row) => {
        const existingBanco = initialRows.find((br) => br.id === row.id);
        return {
          id: row.id,
          sistemaId: currentSystemId || '',
          tipo: row.tipo,
          acessoContainers: existingBanco?.acessoContainers || [],
        };
      });
      onBancosDeDadosChange(updatedBancos);
    }
  };

  const handleAddNew = () => {
    setEditingRow(null);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setEditingRow(null);
  };

  const handleSaveModal = (values: BancoDeDados, editId?: string) => {
    let newRows: Row[] = [];
    if (editId && editingRow) {
      // Editando item existente
      newRows = rows.map((r) =>
        r.id === editId
          ? {
            id: editId,
            tipo: values.tipo,
          }
          : r
      );
    } else {
      // Criando novo item
      const newRow: Row = {
        id: values.id || String(Date.now()),
        tipo: values.tipo,
      };
      newRows = [...rows, newRow];
    }

    setRows(newRows);

    if (onBancosDeDadosChange) {
      const updatedBancos = newRows.map((row) => {
        // Se é o row que acabou de ser salvo, usar os acessoContainers do values
        if (
          (editId && row.id === editId) ||
          (!editId && row.id === newRows[newRows.length - 1].id)
        ) {
          return {
            id: row.id,
            sistemaId: currentSystemId || '',
            tipo: row.tipo,
            acessoContainers: values.acessoContainers || [],
          };
        }
        // Para outros rows, manter os acessoContainers existentes
        const existingBanco = initialRows.find((br) => br.id === row.id);
        return {
          id: row.id,
          sistemaId: currentSystemId || '',
          tipo: row.tipo,
          acessoContainers: existingBanco?.acessoContainers || [],
        };
      });
      onBancosDeDadosChange(updatedBancos);
    }

    handleCloseModal();
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="flex-end" gap={4}>
      {isLoading ? (
        <Skeleton variant="rectangular" width={80} height={36} />
      ) : (
        <Button
          variant="contained"
          color="primary"
          sx={{ width: '80px' }}
          onClick={handleAddNew}
          startIcon={<AddIcon />}
        >
          Novo
        </Button>
      )}

      <TableContainer component={Paper} elevation={1}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={100}>Ações</TableCell>
              <TableCell>Tipo</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <>
                {[1, 2, 3].map((n) => (
                  <TableRow key={n}>
                    <TableCell>
                      <Skeleton variant="rectangular" width={80} height={30} />
                    </TableCell>
                    <TableCell>
                      <Skeleton variant="text" width={200} />
                    </TableCell>
                  </TableRow>
                ))}
              </>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={2}
                  align="center"
                  sx={{ py: 4, color: 'text.secondary' }}
                >
                  Nenhum banco de dados cadastrado
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell>
                    <Tooltip title="Editar">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(row.id)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(row.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>{getTipoBancoDadosNome(row.tipo)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ModalBancoDeDados
        open={openModal}
        onClose={handleCloseModal}
        onSave={handleSaveModal}
        currentSystemId={currentSystemId}
        initialValues={
          editingRow
            ? {
              id: editingRow.id,
              tipo: editingRow.tipo,
              acessoContainers:
                  initialRows.find((r) => r.id === editingRow.id)
                    ?.acessoContainers || [],
              componentesGerais:
                  // Extrair componentes do primeiro container para componentesGerais
                  initialRows.find((r) => r.id === editingRow.id)
                    ?.acessoContainers?.[0]?.acessoComponentes || [],
            }
            : undefined
        }
      />
    </Box>
  );
};
